import React from 'react';
import { usePortalLanguage } from '../../../contexts/PortalLanguageContext';

// Only leaves are checkable (a parent is a heading over its children), so only
// leaves count. Counting headings kept a nested checklist from ever reading
// complete.
const countSelf = (node, completed) => {
    const hasChildren = Array.isArray(node?.children) && node.children.length > 0;
    if (!hasChildren) {
        if (!node?.id) return { total: 0, done: 0 };
        return { total: 1, done: completed[node.id] ? 1 : 0 };
    }
    let total = 0;
    let done = 0;
    node.children.forEach(c => {
        const sub = countSelf(c, completed);
        total += sub.total;
        done += sub.done;
    });
    return { total, done };
};

export const AUTO_COLLAPSE_DELAY_MS = 800;

function Chevron({ open }) {
    return (
        <svg
            viewBox="0 0 20 20"
            fill="currentColor"
            className="h-5 w-5 flex-shrink-0 text-[color:var(--text-muted)] transition-transform duration-200"
            style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
            aria-hidden="true"
        >
            <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
        </svg>
    );
}

function ChecklistNode({ node, depth, completed, busy, onToggle }) {
    const isLeaf = !Array.isArray(node.children) || node.children.length === 0;
    const checked = !!completed[node.id];
    const counts = isLeaf ? null : countSelf(node, completed);
    return (
        <li className={`py-1 ${depth > 0 ? 'pl-4 border-l border-[color:var(--border-color)] ml-2' : ''}`}>
            <label className="flex items-start gap-2 text-sm text-[color:var(--text-primary)] cursor-pointer">
                {isLeaf ? (
                    <input
                        type="checkbox"
                        checked={checked}
                        disabled={busy[node.id]}
                        onChange={(e) => onToggle(node.id, e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-[color:var(--border-color)] text-[color:var(--color-accent)] focus:ring-[color:var(--color-accent)]/25"
                    />
                ) : (
                    <span className="mt-0.5 inline-block w-4 text-xs text-[color:var(--text-muted)]">·</span>
                )}
                <span className="flex-1">
                    <span className={checked ? 'line-through text-[color:var(--text-muted)]' : ''}>{node.text}</span>
                    {!isLeaf && (
                        <span className="ml-2 text-xs text-[color:var(--text-muted)]">{counts.done}/{counts.total}</span>
                    )}
                </span>
            </label>
            {!isLeaf && (
                <ul>
                    {node.children.map(child => (
                        <ChecklistNode key={child.id} node={child} depth={depth + 1} completed={completed} busy={busy} onToggle={onToggle} />
                    ))}
                </ul>
            )}
        </li>
    );
}

function ChecklistSection({ checklist, sessionToken, updateItem, onSessionExpired, onItemPersisted, isCompactLayout }) {
    const { t } = usePortalLanguage();
    // Local optimistic completion state, seeded from server completionState
    const initialCompleted = React.useMemo(() => {
        const out = {};
        Object.entries(checklist.completionState || {}).forEach(([id, entry]) => {
            if (entry?.completedAt) out[id] = true;
        });
        return out;
    }, [checklist.completionState]);

    const [completed, setCompleted] = React.useState(initialCompleted);
    const [busy, setBusy] = React.useState({});
    const [error, setError] = React.useState('');
    const debounceTimers = React.useRef({});

    React.useEffect(() => {
        setCompleted(initialCompleted);
    }, [initialCompleted]);

    const persistToggle = React.useCallback(async (itemId, newCompleted) => {
        setBusy(prev => ({ ...prev, [itemId]: true }));
        try {
            await updateItem({ sessionToken: sessionToken?.jwt, itemId, completed: newCompleted });
            setError('');
            onItemPersisted?.(itemId, newCompleted);
        } catch (err) {
            const code = err?.code || '';
            if (code === 'functions/unauthenticated' || code === 'unauthenticated') {
                // Queue the desired state for after re-verify; keep optimistic UI as-is.
                const queued = JSON.parse(window.sessionStorage.getItem('portalPendingToggles') || '{}');
                queued[itemId] = { completed: newCompleted };
                window.sessionStorage.setItem('portalPendingToggles', JSON.stringify(queued));
                onSessionExpired?.();
                return;
            }
            // Roll back on non-expiry errors
            setCompleted(prev => ({ ...prev, [itemId]: !newCompleted }));
            const msg = code === 'functions/not-found'
                ? 'This item is no longer in your checklist.'
                : "Couldn't save. Try again.";
            setError(msg);
        } finally {
            setBusy(prev => {
                const next = { ...prev };
                delete next[itemId];
                return next;
            });
        }
    }, [updateItem, sessionToken, onSessionExpired, onItemPersisted]);

    const handleToggle = (itemId, newValue) => {
        setCompleted(prev => ({ ...prev, [itemId]: newValue }));
        if (debounceTimers.current[itemId]) clearTimeout(debounceTimers.current[itemId]);
        debounceTimers.current[itemId] = setTimeout(() => {
            persistToggle(itemId, newValue);
            delete debounceTimers.current[itemId];
        }, 300);
    };

    const totals = countSelf({ children: checklist.items }, completed);
    const isComplete = totals.total > 0 && totals.done === totals.total;

    // A finished checklist starts collapsed, and collapses itself the moment its
    // last item is checked, so the list a client scrolls stays short. The
    // header toggles it either way.
    const [collapsed, setCollapsed] = React.useState(isComplete);
    const wasComplete = React.useRef(isComplete);
    React.useEffect(() => {
        if (isComplete === wasComplete.current) return undefined;
        wasComplete.current = isComplete;
        if (!isComplete) {
            setCollapsed(false);
            return undefined;
        }
        // Let the last check mark land before the card folds away.
        const timer = setTimeout(() => setCollapsed(true), AUTO_COLLAPSE_DELAY_MS);
        return () => clearTimeout(timer);
    }, [isComplete]);

    const bodyId = `portal-checklist-${checklist.id}`;
    const name = checklist.name;

    return (
        <div
            data-testid="portal-checklist"
            className={`rounded-2xl border border-[color:var(--border-color)] bg-[color:var(--surface-primary)] shadow-sm ${isCompactLayout ? 'p-5' : 'p-6'}`}
        >
            <button
                type="button"
                onClick={() => setCollapsed(c => !c)}
                aria-expanded={!collapsed}
                aria-controls={bodyId}
                aria-label={t(collapsed ? 'Show {name}' : 'Hide {name}', { name })}
                className="flex w-full items-center justify-between gap-3 text-left"
            >
                <h2 className="min-w-0 text-lg font-semibold text-[color:var(--text-primary)]">{name}</h2>
                <span className="flex flex-shrink-0 items-center gap-2">
                    {isComplete ? (
                        <span className="rounded-full bg-[color:var(--surface-success)] px-2 py-0.5 text-xs font-semibold text-[color:var(--color-primary)]">
                            {t('Complete')}
                        </span>
                    ) : (
                        <span className="text-xs text-[color:var(--text-muted)]">{t('{done}/{total} complete', { done: totals.done, total: totals.total })}</span>
                    )}
                    <Chevron open={!collapsed} />
                </span>
            </button>
            {error && (
                <div className="mt-3 rounded-md border border-[color:var(--color-danger)]/20 bg-[color:var(--surface-danger)] px-3 py-2 text-sm text-[color:var(--color-danger)]">
                    {t(error)}
                </div>
            )}
            {!collapsed && (
                <ul id={bodyId} className="mt-4 space-y-1">
                    {checklist.items.map(node => (
                        <ChecklistNode key={node.id} node={node} depth={0} completed={completed} busy={busy} onToggle={handleToggle} />
                    ))}
                </ul>
            )}
        </div>
    );
}

export default ChecklistSection;
